import { Quote } from "lucide-react";
import Reveal from "@/components/common/Reveal";
import MagneticButton from "@/components/common/MagneticButton";
import founderPhoto from "@/assets/founder/mudit-pratap-singh.png";

const FOUNDER_IMG = founderPhoto;

export default function Founder() {
  return (
    <section id="founder" className="section relative overflow-hidden" data-testid="founder-section">
      <div className="absolute inset-0 aurora opacity-30" />
      <div className="container-x relative">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          <Reveal direction="up" className="lg:col-span-5">
            <div className="flex flex-col items-center gap-8">
              <div className="relative group">
                <div
                  className="pointer-events-none absolute -inset-6 rounded-full opacity-60 blur-2xl transition duration-500 group-hover:opacity-100"
                  style={{ background: "radial-gradient(circle, var(--brand-glow), transparent 60%)" }}
                />
                <div
                  className="relative h-48 w-48 sm:h-56 sm:w-56 rounded-full overflow-hidden glass-strong"
                  style={{ boxShadow: "0 0 0 2px var(--glass-border), 0 30px 60px -20px var(--brand-glow)" }}
                >
                  <img
                    src={FOUNDER_IMG}
                    alt="Mudit Pratap Singh — Founder of DIBRAL"
                    data-testid="founder-image"
                    className="w-full h-full object-cover transition-all duration-700 group-hover:grayscale-0"
                    style={{ filter: "grayscale(100%)" }}
                    loading="lazy"
                  />
                </div>

                <div className="hidden md:flex absolute -top-2 -right-20 glass-strong rounded-full px-3.5 py-1.5 text-xs text-[var(--body-fg)]/75 items-center gap-2 whitespace-nowrap float-chip">
                  <span
                    className="h-2 w-2 rounded-full"
                    style={{ background: "var(--brand)", boxShadow: "0 0 8px var(--brand)" }}
                  />
                  Open to new partnerships
                </div>
              </div>

              <div className="w-full max-w-sm space-y-3">
                <div className="glass glow-border rounded-2xl px-4 py-3 flex items-center justify-between tilt-card">
                  <div className="flex items-center gap-3">
                    <span className="avatar-orb h-9 w-9 text-xs">MS</span>
                    <div>
                      <div className="text-[var(--body-fg)] font-semibold tracking-tight">Mudit Pratap Singh</div>
                      <div className="text-xs text-[var(--brand)]/80">Founder & CEO, DIBRAL</div>
                    </div>
                  </div>
                  <span className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-[var(--glass-border)] text-[var(--brand)]">
                    <Quote className="w-3.5 h-3.5" />
                  </span>
                </div>

                <div className="glass glow-border rounded-2xl px-4 py-3 flex items-center gap-3 tilt-card">
                  <span className="avatar-orb h-9 w-9 text-xs">AS</span>
                  <div>
                    <div className="text-[var(--body-fg)] font-semibold tracking-tight">Ayush Singh</div>
                    <div className="text-xs text-[var(--brand)]/80">Co-founder, DIBRAL</div>
                  </div>
                </div>
              </div>
            </div>
          </Reveal>

          <Reveal direction="up" delay={0.12} className="lg:col-span-7">
            <div>
              <span className="eyebrow">
                <span className="dot" /> Founder
              </span>
              <h2 className="h-display mt-5 text-4xl sm:text-5xl lg:text-6xl text-[var(--body-fg)]">
                Built by an{" "}
                <span className="font-serif italic gradient-text">operator</span>,
                for operators.
              </h2>

              <div className="mt-7 space-y-5 text-[var(--body-fg)]/70 leading-relaxed">
                <p>
                  I started DIBRAL after watching too many incredible small businesses lose
                  deals — not because their product was weak, but because their operations
                  couldn't keep up. A missed WhatsApp reply, a forgotten follow-up, a lead
                  stuck in someone's inbox.
                </p>
                <p>
                  <span className="text-[var(--body-fg)]">Our vision</span> is simple: every ambitious
                  SMB should have access to the same calibre of AI workforce that
                  billion-dollar companies enjoy — without the complexity, the consultants
                  or the bloated price tag.
                </p>
                <p>
                  <span className="text-[var(--body-fg)]">Our mission</span> is to deliver business
                  automation that actually moves the numbers — more leads, faster sales,
                  happier customers — measured every single month.
                </p>
                <p className="font-serif italic text-xl text-[var(--body-fg)]/90 leading-snug pt-2">
                  "If your business still depends on you replying to every message, you don't have a business. You have a job."
                </p>
              </div>

              <div className="mt-9 flex flex-wrap gap-3">
                <MagneticButton
                  as="a"
                  href="#booking"
                  className="btn-primary"
                  data-testid="founder-book-btn"
                >
                  Book a call with me
                </MagneticButton>
                <MagneticButton
                  as="a"
                  href="mailto:Muditprataps20138@gmail.com"
                  className="btn-secondary"
                >
                  Email me directly
                </MagneticButton>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
